import React from 'react';
import DojoAdminWireframes from './DojoAdminWireframes';
import {
  CoachDashboard, CoachQR, CoachEarnings, CoachExercise,
  CoachLeave, CoachProfile, CoachPayments,
  StudentLanding, StudentSignup, StudentOTP, StudentLogin,
  StudentDashboard, ParentView, StudentQR, StudentProgress,
  StudentPayments, StudentProfile,
} from './DojoMobileWireframes';

const F = { h: "'Poppins', sans-serif", b: "'Inter', sans-serif", n: "'Montserrat', sans-serif" };
const C = { primary: '#C41E3A', dark: '#2D3748', darker: '#1A202C', success: '#38A169', warning: '#DD6B20', muted: '#718096', border: '#E2E8F0', bg: '#F7FAFC', white: '#FFFFFF' };

/* ─── Admin screen at ~62% scale ─────────────────────── */
const ADMIN_SCALE = 0.62;
const ADMIN_SRC_W = 1300;
const ADMIN_SRC_H = 900;

function AdminScreen({ screen, caption, step }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, flexShrink: 0 }}>
      {step && <StepBadge n={step} />}
      <div style={{
        width: ADMIN_SRC_W * ADMIN_SCALE,
        height: ADMIN_SRC_H * ADMIN_SCALE,
        overflow: 'hidden',
        borderRadius: 12,
        border: `1px solid ${C.border}`,
        boxShadow: '0 6px 32px rgba(0,0,0,0.14)',
        flexShrink: 0,
        position: 'relative',
      }}>
        <div style={{
          width: ADMIN_SRC_W,
          height: ADMIN_SRC_H,
          transform: `scale(${ADMIN_SCALE})`,
          transformOrigin: 'top left',
          pointerEvents: 'none',
          userSelect: 'none',
        }}>
          <DojoAdminWireframes initialScreen={screen} />
        </div>
      </div>
      {caption && (
        <div style={{ fontFamily: F.b, fontSize: 12, color: C.muted, textAlign: 'center', maxWidth: ADMIN_SRC_W * ADMIN_SCALE, lineHeight: 1.5 }}>{caption}</div>
      )}
    </div>
  );
}

/* ─── Mobile screen (natural PhoneFrame size) ─────────── */
function MobileScreen({ Component, caption, step }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, flexShrink: 0 }}>
      {step && <StepBadge n={step} />}
      <div style={{ pointerEvents: 'none', userSelect: 'none', flexShrink: 0 }}>
        <Component />
      </div>
      {caption && (
        <div style={{ fontFamily: F.b, fontSize: 12, color: C.muted, textAlign: 'center', maxWidth: 340, lineHeight: 1.5 }}>{caption}</div>
      )}
    </div>
  );
}

function StepBadge({ n }) {
  return (
    <div style={{ width: 28, height: 28, borderRadius: 14, background: C.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: F.n, fontSize: 13, fontWeight: 800, flexShrink: 0 }}>{n}</div>
  );
}

function Arr() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', paddingTop: 40, flexShrink: 0, color: C.primary, fontSize: 32, fontWeight: 300, padding: '40px 8px 0' }}>→</div>
  );
}

function FlowStrip({ title, desc, children }) {
  return (
    <div style={{ marginBottom: 48 }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontFamily: F.h, fontSize: 18, fontWeight: 700, color: C.dark }}>{title}</div>
        {desc && <div style={{ fontFamily: F.b, fontSize: 13, color: C.muted, marginTop: 3 }}>{desc}</div>}
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, overflowX: 'auto', paddingBottom: 12, paddingTop: 4 }}>
        {children}
      </div>
    </div>
  );
}

function Section({ emoji, title, sub, children, dark }) {
  return (
    <section style={{ background: dark ? 'linear-gradient(135deg, #1A202C, #2D3748)' : C.white, borderRadius: 20, padding: '44px 52px', marginBottom: 36, boxShadow: '0 2px 24px rgba(0,0,0,0.07)', border: `1px solid ${C.border}` }}>
      <div style={{ marginBottom: 32 }}>
        <div style={{ fontFamily: F.h, fontSize: 28, fontWeight: 800, color: dark ? C.white : C.dark, marginBottom: 4 }}>{emoji} {title}</div>
        {sub && <div style={{ fontFamily: F.b, fontSize: 14, color: dark ? 'rgba(255,255,255,0.55)' : C.muted }}>{sub}</div>}
      </div>
      {children}
    </section>
  );
}

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
export default function DojoMasterPresentation() {
  return (
    <div style={{ minHeight: '100vh', background: '#ECEEF2', fontFamily: F.b }}>

      {/* ── COVER ── */}
      <div style={{ background: 'linear-gradient(135deg, #1A202C 0%, #4A1942 50%, #C41E3A 100%)', padding: '80px 48px 64px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'repeating-linear-gradient(45deg, #fff 0, #fff 1px, transparent 0, transparent 50%)', backgroundSize: '20px 20px' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>🥋</div>
          <div style={{ fontFamily: F.h, fontSize: 52, fontWeight: 900, color: '#fff', lineHeight: 1.1, marginBottom: 12 }}>DojoMaster</div>
          <div style={{ fontFamily: F.h, fontSize: 20, fontWeight: 400, color: 'rgba(255,255,255,0.7)', marginBottom: 10 }}>Martial Arts Studio Management System</div>
          <div style={{ fontFamily: F.b, fontSize: 13, color: 'rgba(255,255,255,0.4)', marginBottom: 48 }}>Product Wireframes &amp; User Flow Review · February 2026</div>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            {[['🖥️','Admin Portal','14 screens','#667eea'],['🥋','Coach App','8 screens',C.primary],['🎓','Student App','11 screens',C.success]].map(([icon, label, sub, col]) => (
              <div key={label} style={{ background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)', borderRadius: 16, padding: '20px 28px', border: `1px solid ${col}55`, minWidth: 140 }}>
                <div style={{ fontSize: 28, marginBottom: 6 }}>{icon}</div>
                <div style={{ fontFamily: F.h, fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 3 }}>{label}</div>
                <div style={{ fontFamily: F.b, fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>{sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '48px 40px' }}>

        {/* ── SYSTEM OVERVIEW ── */}
        <Section emoji="🏗️" title="System Overview" sub="Three interconnected applications sharing one backend" dark>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 24, flexWrap: 'wrap', marginBottom: 32 }}>
            <div style={{ background: 'rgba(102,126,234,0.15)', border: '2px solid #667eea', borderRadius: 16, padding: '24px 28px', minWidth: 190, textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🖥️</div>
              <div style={{ fontFamily: F.h, fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 10 }}>Admin Portal</div>
              {['React Web + Tablet','Login / Dashboard','Students & Coaches','Sessions & Finance','Belt Exams & Leave'].map(t => <div key={t} style={{ fontFamily: F.b, fontSize: 11, color: 'rgba(255,255,255,0.6)', lineHeight: 2 }}>{t}</div>)}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
              <div style={{ fontFamily: F.b, fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>ASP.NET Core API</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 60, height: 2, background: C.primary }} />
                <div style={{ background: C.primary, color: '#fff', borderRadius: 12, padding: '10px 16px', fontFamily: F.h, fontSize: 12, fontWeight: 700, textAlign: 'center', lineHeight: 1.8 }}>🗄️ PostgreSQL</div>
                <div style={{ width: 60, height: 2, background: C.primary }} />
              </div>
              <div style={{ fontFamily: F.b, fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Firebase FCM</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {[[C.primary,'🥋','Coach App','Flutter Mobile','QR Code Display','Live Attendance','Earnings & Exercises'],[C.success,'🎓','Student / Parent App','Flutter Mobile','QR Scan Check-In','Progress & Badges','Benefit Pay']].map(([col, icon, label, ...lines]) => (
                <div key={label} style={{ background: col + '22', border: `2px solid ${col}`, borderRadius: 16, padding: '16px 24px', textAlign: 'center' }}>
                  <div style={{ fontSize: 26, marginBottom: 6 }}>{icon}</div>
                  <div style={{ fontFamily: F.h, fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 6 }}>{label}</div>
                  {lines.map(l => <div key={l} style={{ fontFamily: F.b, fontSize: 11, color: 'rgba(255,255,255,0.6)', lineHeight: 2 }}>{l}</div>)}
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
            {[['💬','WhatsApp Business','Automated reminders'],['💳','Benefit Pay (Tap)','Student payments'],['🔔','Firebase FCM','Push notifications'],['📷','QR Code Engine','Attendance check-in']].map(([icon, label, sub]) => (
              <div key={label} style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, padding: '12px 20px', textAlign: 'center', minWidth: 140 }}>
                <div style={{ fontSize: 20, marginBottom: 4 }}>{icon}</div>
                <div style={{ fontFamily: F.b, fontSize: 11, fontWeight: 600, color: '#fff' }}>{label}</div>
                <div style={{ fontFamily: F.b, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>{sub}</div>
              </div>
            ))}
          </div>
        </Section>

        {/* ══════════════════════════════════════
            ADMIN PORTAL FLOWS
        ══════════════════════════════════════ */}
        <Section emoji="🖥️" title="Admin Portal Flows" sub="Web & tablet interface — manage every aspect of the studio">

          <FlowStrip title="1 · Login & Dashboard" desc="Admin logs in and lands on the command centre with live KPIs and today's session schedule">
            <AdminScreen step={1} screen="admin-login" caption="Role selector + secure login" />
            <Arr />
            <AdminScreen step={2} screen="dashboard" caption="Live KPIs — students, sessions, revenue, attendance" />
            <Arr />
            <AdminScreen step={3} screen="daily-sessions" caption="Today's active sessions with live check-in counts" />
          </FlowStrip>

          <FlowStrip title="2 · Student Management" desc="View all students, drill into a profile, and schedule a belt exam when the student is eligible">
            <AdminScreen step={1} screen="students" caption="Searchable student roster with belt, payment, and attendance status" />
            <Arr />
            <AdminScreen step={2} screen="student-profile" caption="Full profile — attendance history, payments, exam eligibility badge" />
            <Arr />
            <AdminScreen step={3} screen="belt-exams" caption="Eligible students list → schedule exam → record pass/fail" />
          </FlowStrip>

          <FlowStrip title="3 · Membership Plans" desc="Manage plan catalogue — pricing, duration, freeze requests">
            <AdminScreen step={1} screen="plans" caption="Plan cards with active student counts, pricing, and trial settings" />
          </FlowStrip>

          <FlowStrip title="4 · Coach Onboarding" desc="Add a coach with commission structure — system auto-creates their app login">
            <AdminScreen step={1} screen="coaches" caption="Active coaches with performance KPIs — sessions, earnings, attendance" />
            <Arr />
            <AdminScreen step={2} screen="add-coach" caption="Personal info + professional details + 4-formula commission + % override" />
            <Arr />
            <AdminScreen step={3} screen="coach-profile" caption="Coach detail — students, session history, commission breakdown" />
          </FlowStrip>

          <FlowStrip title="5 · Session & Attendance" desc="Admin schedules sessions → coach starts QR → students scan → attendance auto-captured">
            <AdminScreen step={1} screen="schedule" caption="Weekly calendar — create and manage class timetable" />
            <Arr />
            <AdminScreen step={2} screen="daily-sessions" caption="Running sessions with real-time check-in counter" />
            <Arr />
            <AdminScreen step={3} screen="kiosk" caption="Reception kiosk terminal — students tap or scan to check in" />
            <Arr />
            <AdminScreen step={4} screen="attendance" caption="Full attendance report — per-session breakdown, export" />
          </FlowStrip>

          <FlowStrip title="6 · Payments & Finance" desc="Collect student fees → calculate commission → process monthly payroll">
            <AdminScreen step={1} screen="payments" caption="Student fees — Paid / Overdue / Due Soon with WhatsApp reminders" />
            <Arr />
            <AdminScreen step={2} screen="commission" caption="Monthly commission payroll — 4 formula types, one-click approval" />
            <Arr />
            <AdminScreen step={3} screen="finance" caption="Revenue, expenses, net income — bar charts + export" />
          </FlowStrip>

          <FlowStrip title="7 · Leave & Substitution" desc="Coach requests leave → admin approves → substitute assigned to affected sessions">
            <AdminScreen step={1} screen="leave-requests" caption="All leave requests — filter by status, approve or reject" />
            <Arr />
            <AdminScreen step={2} screen="substitute" caption="Assign a replacement coach to the affected session slots" />
          </FlowStrip>

        </Section>

        {/* ══════════════════════════════════════
            COACH APP FLOWS
        ══════════════════════════════════════ */}
        <Section emoji="🥋" title="Coach Mobile App" sub="Flutter app — start sessions, track earnings, assign exercises, follow up on payments">

          <FlowStrip title="1 · Running a Session" desc="Coach opens app → displays QR for students to scan → monitors live check-ins → ends session">
            <MobileScreen step={1} Component={CoachDashboard} caption="Today's schedule, monthly student count and earnings" />
            <Arr />
            <MobileScreen step={2} Component={CoachQR} caption="Phone 1: Full-screen QR + live counter + End Session · Phone 2: Live attendance viewer" />
          </FlowStrip>

          <FlowStrip title="2 · Earnings & Session Detail" desc="Monthly summary → tap any session row → per-student BHD breakdown">
            <MobileScreen step={1} Component={CoachEarnings} caption="Monthly total + session list — tap any row to drill in" />
          </FlowStrip>

          <FlowStrip title="3 · Exercise Assignment" desc="Browse shared library by discipline → assign to a student in one tap">
            <MobileScreen step={1} Component={CoachExercise} caption="Phone 1: Assign form (From Library / Custom tabs) · Phone 2: Tracker · Phone 3: Exercise Library with discipline filter" />
          </FlowStrip>

          <FlowStrip title="4 · Student Payment Follow-up" desc="See which students are overdue → send pre-filled WhatsApp reminder">
            <MobileScreen step={1} Component={CoachPayments} caption="Payment status list (Paid/Overdue/Due Soon) + WhatsApp compose" />
          </FlowStrip>

          <FlowStrip title="5 · Leave Request" desc="Coach requests leave — admin is notified and can approve or reject">
            <MobileScreen step={1} Component={CoachLeave} caption="Submit leave request with date range and reason" />
          </FlowStrip>

          <FlowStrip title="6 · Coach Profile" desc="Personal info, professional credentials, this-month earnings snapshot">
            <MobileScreen step={1} Component={CoachProfile} caption="Hero stats → personal & professional info → earnings card" />
          </FlowStrip>

        </Section>

        {/* ══════════════════════════════════════
            STUDENT / PARENT APP FLOWS
        ══════════════════════════════════════ */}
        <Section emoji="🎓" title="Student / Parent App" sub="Flutter app — sign up, QR check-in, track belt progress, pay fees, earn badges">

          <FlowStrip title="1 · New Student Onboarding" desc="Download app → register → choose plan → pay via Benefit Pay → verify phone → start training">
            <MobileScreen step={1} Component={StudentLanding} caption="App landing — Sign Up or Log In" />
            <Arr />
            <MobileScreen step={2} Component={StudentSignup} caption="Phone 1: Personal info + plan selection · Phone 2: Benefit Pay payment" />
            <Arr />
            <MobileScreen step={3} Component={StudentOTP} caption="6-digit OTP verification — code sent to mobile number" />
          </FlowStrip>

          <FlowStrip title="2 · Returning Student Login" desc="Role selection → Student gets OTP login, Coach uses ID + Password">
            <MobileScreen step={1} Component={StudentLogin} caption="Choose Coach or Student — each has a different login method" />
          </FlowStrip>

          <FlowStrip title="3 · Daily Check-In" desc="Student opens dashboard → scans coach's QR code → instant confirmation + streak">
            <MobileScreen step={1} Component={StudentDashboard} caption="Home screen — today's class card + quick scan shortcut" />
            <Arr />
            <MobileScreen step={2} Component={StudentQR} caption="Phone 1: QR camera scanner · Phone 2: Check-In success + streak · Phone 3: All 4 error states" />
          </FlowStrip>

          <FlowStrip title="4 · Progress & Achievements" desc="Hours tracked automatically → belt journey → earn badges for attendance milestones">
            <MobileScreen step={1} Component={StudentProgress} caption="Phone 1: Belt progress + calendar · Phone 2: Achievement badges (earned & locked)" />
          </FlowStrip>

          <FlowStrip title="5 · Fee Payment" desc="View upcoming fees → pay instantly via Benefit Pay — parents can do this too">
            <MobileScreen step={1} Component={StudentPayments} caption="Fee history + due status + Benefit Pay button" />
          </FlowStrip>

          <FlowStrip title="6 · Student Profile" desc="Personal info, membership details, session history">
            <MobileScreen step={1} Component={StudentProfile} caption="Profile overview — plan, belt, attendance stats" />
          </FlowStrip>

          <FlowStrip title="7 · Parent View" desc="Parent monitors multiple children under one login — read-only, fully transparent">
            <MobileScreen step={1} Component={ParentView} caption="Multi-child switcher — attendance, payments, progress per child" />
          </FlowStrip>

        </Section>

        {/* ── KEY FEATURES ── */}
        <Section emoji="⚡" title="Key Feature Highlights" sub="Core differentiators that make DojoMaster stand out">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 18 }}>
            {[
              { icon:'📷', title:'QR Attendance System', color:C.primary, desc:'Coach displays a session-unique QR. Students scan at the door — instant check-in, auto-logged, admin sees live count in real time.', tags:['Real-time','Zero friction','Auto-logged'] },
              { icon:'🏆', title:'Belt Exam Engine', color:'#805AD5', desc:'Hours accumulate per class automatically. When a student hits the threshold, admin is alerted. Pass/fail recorded and belt promoted.', tags:['Hours-based','Auto-notify','Belt promotion'] },
              { icon:'💰', title:'4-Formula Commission', color:C.success, desc:'Per Student, Per Session, Fixed Monthly, or Hybrid (base + %). Each coach can have a different structure. One-click monthly payroll.', tags:['Flexible','Custom per coach','One-click payroll'] },
              { icon:'💬', title:'WhatsApp Automation', color:'#25D366', desc:'Payment reminders, leave approvals, and exam alerts sent via WhatsApp Business API. Coaches can also send nudges from their app.', tags:['Auto-reminders','Template-based','Coach + Admin'] },
              { icon:'💳', title:'Benefit Pay Integration', color:'#0072BC', desc:"Students and parents pay via Benefit Pay — Bahrain's most-used payment method. Requests sent automatically when fees are due.", tags:['Bahrain-native','Instant','Auto-request'] },
              { icon:'👨‍👩‍👧', title:'Parent View', color:C.warning, desc:'Parents monitor multiple children — attendance, progress, belt journey, and upcoming payments. Read-only and fully transparent.', tags:['Multi-child','Read-only','Transparent'] },
            ].map(f => (
              <div key={f.title} style={{ background: C.white, borderRadius: 16, padding: '24px', border: `1px solid ${C.border}`, borderTop: `4px solid ${f.color}` }}>
                <div style={{ fontSize: 30, marginBottom: 12 }}>{f.icon}</div>
                <div style={{ fontFamily: F.h, fontSize: 15, fontWeight: 700, color: C.dark, marginBottom: 10 }}>{f.title}</div>
                <div style={{ fontFamily: F.b, fontSize: 13, color: C.muted, lineHeight: 1.7, marginBottom: 14 }}>{f.desc}</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {f.tags.map(tag => <span key={tag} style={{ background: f.color + '18', color: f.color, border: `1px solid ${f.color}44`, borderRadius: 20, padding: '3px 10px', fontFamily: F.b, fontSize: 11, fontWeight: 600 }}>{tag}</span>)}
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* ── NEXT STEPS ── */}
        <Section emoji="🚀" title="Next Steps" sub="What we'd like to agree on before development begins">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 16 }}>
            {[
              { n:'01', icon:'✅', label:'Wireframe Sign-off', desc:'Review and approve all screens before development starts' },
              { n:'02', icon:'🎨', label:'Content & Branding', desc:'Club logo, colours, and sample data (coaches, plans, belts)' },
              { n:'03', icon:'💰', label:'Commission Rules', desc:'Confirm formula type per coach and any club-specific rules' },
              { n:'04', icon:'💳', label:'Benefit Pay Setup', desc:'Provide merchant credentials for payment collection' },
              { n:'05', icon:'💬', label:'WhatsApp API Access', desc:'Connect Business account for automated notifications' },
              { n:'06', icon:'🏁', label:'Development Kickoff', desc:'Agree timeline, milestones, and testing plan for sprint 1' },
            ].map(s => (
              <div key={s.n} style={{ background: C.bg, borderRadius: 14, padding: '20px', border: `1px solid ${C.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 17, background: C.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: F.n, fontSize: 13, fontWeight: 800, color: '#fff', flexShrink: 0 }}>{s.n}</div>
                  <span style={{ fontSize: 18 }}>{s.icon}</span>
                  <div style={{ fontFamily: F.h, fontSize: 13, fontWeight: 700, color: C.dark }}>{s.label}</div>
                </div>
                <div style={{ fontFamily: F.b, fontSize: 12, color: C.muted, lineHeight: 1.6, paddingLeft: 44 }}>{s.desc}</div>
              </div>
            ))}
          </div>
        </Section>

        {/* Footer */}
        <div style={{ textAlign: 'center', padding: '32px 0 20px', fontFamily: F.b, fontSize: 12, color: C.muted }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>🥋</div>
          <div style={{ fontFamily: F.h, fontSize: 16, fontWeight: 700, color: C.dark, marginBottom: 4 }}>DojoMaster</div>
          <div>Product Wireframes · Confidential · February 2026</div>
        </div>
      </div>
    </div>
  );
}
